import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useComplianceAnalysis } from '../../../hooks/useComplianceAnalysis';

/**
 * AI-powered compliance analysis panel component
 * Provides real-time compliance checking and suggestions
 */
const AICompliancePanel = ({ 
  selectedContent = null,
  onAnalysisComplete = () => {},
  onSuggestionApply = () => {},
  className = '' 
}) => {
  const [contentText, setContentText] = useState('');
  const [platform, setPlatform] = useState('social_media');
  const [contentType, setContentType] = useState('marketing');
  const [activeTab, setActiveTab] = useState('analyze');
  const [suggestions, setSuggestions] = useState(null);

  const {
    isAnalyzing,
    analysis,
    error,
    analyzeContent,
    generateSuggestions,
    clearError
  } = useComplianceAnalysis();

  // Update content text when selected content changes
  useEffect(() => {
    if (selectedContent?.preview || selectedContent?.text) {
      setContentText(selectedContent?.preview || selectedContent?.text);
      setPlatform(selectedContent?.platform?.toLowerCase() || 'social_media');
    }
  }, [selectedContent]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(clearError, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  const handleAnalyze = async () => {
    if (!contentText?.trim()) {
      return;
    }

    const result = await analyzeContent(contentText, platform, contentType);
    if (result) {
      onAnalysisComplete(result);
    }
  };

  const handleGenerateSuggestions = async () => {
    if (!analysis?.violations?.length) {
      return;
    }

    const result = await generateSuggestions(contentText, analysis?.violations, platform);
    if (result) {
      setSuggestions(result);
      setActiveTab('suggestions');
    }
  };

  const handleApplySuggestion = (suggestion) => {
    setContentText(suggestion?.content);
    onSuggestionApply(suggestion);
  };

  const getRiskColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'border-red-500 bg-red-50';
      case 'high': return 'border-orange-500 bg-orange-50';
      case 'medium': return 'border-yellow-500 bg-yellow-50';
      case 'low': return 'border-green-500 bg-green-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  const tabs = [
    { id: 'analyze', label: 'AI Analysis', icon: 'Brain' },
    { id: 'suggestions', label: 'AI Suggestions', icon: 'Lightbulb', disabled: !suggestions },
  ];

  return (
    <div className={`bg-card border border-border rounded-lg h-full flex flex-col ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Icon name="Brain" size={20} className="text-purple-600" />
            <h3 className="font-semibold text-foreground">AI Compliance Assistant</h3>
          </div>
          <div className="flex items-center space-x-1 text-xs text-muted-foreground">
            <Icon name="Sparkles" size={14} />
            <span>Powered by GPT-5</span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-muted rounded-lg p-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => !tab?.disabled && setActiveTab(tab?.id)}
              disabled={tab?.disabled}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'bg-card text-foreground shadow-sm'
                  : tab?.disabled
                  ? 'text-muted-foreground/50 cursor-not-allowed'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Error Display */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}
      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'analyze' && (
          <div className="h-full flex flex-col">
            {/* Input Section */}
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Content to Analyze
                </label>
                <textarea
                  value={contentText}
                  onChange={(e) => setContentText(e?.target?.value)}
                  placeholder="Enter or paste content to analyze for compliance..."
                  className="w-full h-24 px-3 py-2 border border-border rounded-md text-sm resize-none focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Platform
                  </label>
                  <select
                    value={platform}
                    onChange={(e) => setPlatform(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="social_media">Social Media</option>
                    <option value="website">Website</option>
                    <option value="email">Email</option>
                    <option value="print">Print</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Content Type
                  </label>
                  <select
                    value={contentType}
                    onChange={(e) => setContentType(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="marketing">Marketing</option>
                    <option value="educational">Educational</option>
                    <option value="disclosure">Disclosure</option>
                    <option value="announcement">Announcement</option>
                  </select>
                </div>
              </div>

              <Button
                onClick={handleAnalyze}
                disabled={!contentText?.trim() || isAnalyzing}
                className="w-full"
                iconName={isAnalyzing ? "Loader2" : "Search"}
                iconClassName={isAnalyzing ? "animate-spin" : ""}
              >
                {isAnalyzing ? 'Analyzing...' : 'Analyze Compliance'}
              </Button>
            </div>

            {/* Analysis Results */}
            {analysis && (
              <div className="flex-1 overflow-y-auto px-4 pb-4">
                {/* Overall Risk */}
                <div className={`p-3 rounded-lg mb-4 ${getRiskColor(analysis?.overallRisk)}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Overall Risk: {analysis?.overallRisk}</div>
                      <div className="text-sm opacity-75">
                        Compliance Score: {analysis?.complianceScore}/100
                      </div>
                    </div>
                    <Icon name="Shield" size={24} />
                  </div>
                </div>

                {/* Violation Counts */}
                {analysis?.violationCounts && (
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {Object?.entries(analysis?.violationCounts)?.map(([severity, count]) => (
                      <div key={severity} className={`p-2 rounded-md border ${getSeverityColor(severity)}`}>
                        <div className="text-xs font-medium capitalize">{severity}</div>
                        <div className="text-lg font-bold">{count}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Violations List */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-foreground">Violations Found</h4>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleGenerateSuggestions}
                      disabled={!analysis?.violations?.length || isAnalyzing}
                      iconName="Lightbulb"
                    >
                      Get AI Suggestions
                    </Button>
                  </div>

                  {analysis?.violations?.map((violation, index) => (
                    <div
                      key={violation?.id || index}
                      className={`p-3 rounded-lg border-l-4 ${getSeverityColor(violation?.severity)}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-medium text-sm text-foreground">
                          {violation?.rule}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getSeverityColor(violation?.severity)}`}>
                          {violation?.severity}
                        </span>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-2">
                        {violation?.description}
                      </p>
                      
                      {violation?.flaggedText && (
                        <div className="text-sm mb-2">
                          <span className="font-medium">Flagged Text: </span>
                          <span className="italic">"{violation?.flaggedText}"</span>
                        </div>
                      )}
                      
                      <div className="text-xs text-muted-foreground">
                        {violation?.regulation}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'suggestions' && suggestions && (
          <div className="h-full overflow-y-auto p-4">
            <div className="mb-4">
              <h4 className="font-medium text-foreground mb-2">AI-Generated Compliant Versions</h4>
              <p className="text-sm text-muted-foreground">
                Choose a compliant version or use as inspiration for your edits.
              </p>
            </div>

            <div className="space-y-4">
              {suggestions?.suggestions?.map((suggestion, index) => (
                <div key={index} className="border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-sm">{suggestion?.version}</span>
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                        Score: {suggestion?.complianceScore}/100
                      </span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleApplySuggestion(suggestion)}
                      iconName="Check"
                    >
                      Apply
                    </Button>
                  </div>

                  <div className="mb-3 p-3 bg-muted rounded-md text-sm">
                    {suggestion?.content}
                  </div>

                  <div className="text-xs text-muted-foreground">
                    <strong>Changes made:</strong> {suggestion?.changes}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AICompliancePanel;